import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { ApiError } from "@/lib/api/client";

const primaryButtonClass =
  "inline-flex h-10 items-center justify-center rounded-lg bg-indigo px-6 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-indigo disabled:hover:shadow-none disabled:active:scale-100";

export function ErrorFallback({ error }: ErrorComponentProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [pending, setPending] = useState(false);
  const isRateLimited = error instanceof ApiError && error.status === 429;
  const message =
    error instanceof ApiError
      ? (error.error ?? error.message)
      : error instanceof Error
        ? error.message
        : "";

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const retry = () => {
    setPending(true);
    void router.invalidate().finally(() => {
      setPending(false);
      headingRef.current?.focus();
    });
  };

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-danger/10">
        <AlertTriangle size={32} className="text-danger" />
      </div>
      <h1 ref={headingRef} tabIndex={-1} className="text-xl font-bold text-heading outline-none">
        {t("common.errorTitle")}
      </h1>
      <p className="max-w-md text-sm text-muted" role="alert">
        {isRateLimited ? t("common.rateLimited") : message || t("common.error")}
      </p>
      <div className="mt-2 flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={retry}
          disabled={pending}
          aria-busy={pending || undefined}
          className={primaryButtonClass}
        >
          {pending ? t("common.loading") : t("common.retry")}
        </button>
        {!isRateLimited && (
          <Link
            to="/apps"
            preload={false}
            className="text-sm font-medium text-indigo hover:text-indigo-bright"
          >
            {t("common.backToApps")}
          </Link>
        )}
      </div>
    </div>
  );
}
